import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, Loader2, Plus, KeyRound, Trash2, Copy, Check, X, Eye, EyeOff } from 'lucide-react';
import api, { formatApiError } from '@/lib/adminApi';
import { useAdminAuth } from '@/context/AdminAuth';
import { toast } from 'sonner';

const ROLES = ['owner', 'admin', 'support', 'finance', 'read-only'];
const ROLE_BADGE = {
  owner: 'bg-brand/15 text-brand',
  admin: 'bg-brand/10 text-brand/80',
  support: 'bg-success/15 text-success',
  finance: 'bg-yellow-400/15 text-yellow-400',
  'read-only': 'bg-white/10 text-white/50',
};

const inputCls = 'w-full rounded-xl border border-white/10 bg-elevated px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-brand/50 focus:outline-none';

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-surface p-6" onClick={(e) => e.stopPropagation()}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
        <button onClick={onClose} className="text-white/40 hover:text-white"><X className="h-5 w-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const CredentialsModal = ({ creds, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal title="Login credentials — shown once" onClose={onClose}>
      <p className="mb-4 text-sm text-white/60">Save these now. For security, passwords are stored encrypted and can't be viewed again — only reset.</p>
      <div className="space-y-2 rounded-xl border border-brand/20 bg-brand/5 p-4 font-mono text-sm">
        <p className="text-white/70">Email: <span data-testid="team-creds-email" className="text-white">{creds.email}</span></p>
        <p className="text-white/70">Password: <span data-testid="team-creds-password" className="text-brand">{creds.password}</span></p>
      </div>
      <button onClick={copy} data-testid="team-creds-copy" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-ink hover:opacity-90">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? 'Copied' : 'Copy credentials'}
      </button>
    </Modal>
  );
};

const AddAdminModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', role: 'admin', password: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/admin/team', { ...form, password: form.password || null });
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Add team member" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="team-add-name" className={inputCls} placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="team-add-email" className={inputCls} type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <select data-testid="team-add-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          {ROLES.map((r) => <option key={r} value={r} className="bg-surface">{r}</option>)}
        </select>
        <input data-testid="team-add-password" className={inputCls} placeholder="Password (leave blank to auto-generate)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <button type="submit" disabled={busy} data-testid="team-add-submit" className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-ink hover:opacity-90 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create admin
        </button>
      </form>
    </Modal>
  );
};

const MyPasswordCard = () => {
  const [form, setForm] = useState({ current_password: '', new_password: '' });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/admin/change-password', form);
      toast.success('Your password has been changed');
      setForm({ current_password: '', new_password: '' });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-surface/70 p-6">
      <h3 className="font-display text-lg font-semibold text-white">My password</h3>
      <p className="mt-1 text-sm text-white/50">Change the password for your own account.</p>
      <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <input data-testid="my-password-current" className={inputCls} type={show ? 'text' : 'password'} placeholder="Current password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
        <div className="relative">
          <input data-testid="my-password-new" className={inputCls} type={show ? 'text' : 'password'} placeholder="New password (min 8 chars)" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
          <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        </div>
        <button type="submit" disabled={busy} data-testid="my-password-submit" className="flex items-center justify-center gap-2 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-ink hover:opacity-90 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update
        </button>
      </form>
    </div>
  );
};

export default function AdminTeam() {
  const { admin } = useAdminAuth();
  const [members, setMembers] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [creds, setCreds] = useState(null);
  const isOwner = admin?.role === 'owner';

  const fetchTeam = useCallback(() => {
    api.get('/admin/team').then((r) => setMembers(r.data.members));
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const resetPassword = async (m) => {
    if (!window.confirm(`Generate a new password for ${m.email}? Their old password stops working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/team/${m.id}/reset-password`, {});
      setCreds({ email: data.email, password: data.password });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.post(`/admin/team/${m.id}/role`, { role });
      toast.success(`${m.email} is now ${role}`);
      fetchTeam();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      fetchTeam();
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete admin ${m.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success('Admin deleted');
      fetchTeam();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!members) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Team &amp; Access</h1>
          <p className="mt-1 text-sm text-white/50">Manage admin console accounts, roles and passwords. Passwords are stored encrypted — they're shown once at creation or reset.</p>
        </div>
        {isOwner && (
          <button onClick={() => setShowAdd(true)} data-testid="team-add-btn" className="flex items-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-ink hover:opacity-90">
            <Plus className="h-4 w-4" /> Add admin
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-5 py-3 font-medium">Admin</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Created</th>
                <th className="px-5 py-3 font-medium">Last login</th>
                {isOwner && <th className="px-5 py-3 text-right font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} data-testid="team-member-row" className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 font-display text-sm font-bold text-brand">{(m.name || m.email)[0].toUpperCase()}</span>
                      <div>
                        <p className="font-medium text-white">{m.name} {m.id === admin?.id && <span className="text-xs text-white/40">(you)</span>}</p>
                        <p className="text-xs text-white/40">{m.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {isOwner && m.id !== admin?.id ? (
                      <select data-testid="team-role-select" value={m.role} onChange={(e) => changeRole(m, e.target.value)} className={`rounded-full border-0 px-2.5 py-1 text-xs font-semibold outline-none ${ROLE_BADGE[m.role]}`} style={{ background: 'rgba(255,255,255,0.06)' }}>
                        {ROLES.map((r) => <option key={r} value={r} className="bg-surface text-white">{r}</option>)}
                      </select>
                    ) : (
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ROLE_BADGE[m.role]}`}>{m.role}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-white/60">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3 text-white/60">{m.last_login_at ? new Date(m.last_login_at).toLocaleString() : 'Never'}</td>
                  {isOwner && (
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => resetPassword(m)} data-testid="team-reset-password" title="Reset password" className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-white/70 hover:bg-white/5">
                          <KeyRound className="h-3.5 w-3.5" /> Reset password
                        </button>
                        {m.id !== admin?.id && (
                          <button onClick={() => remove(m)} data-testid="team-delete" title="Delete admin" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-destructive/10 hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MyPasswordCard />

      <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-surface/40 p-5">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
        <p className="text-xs leading-relaxed text-white/50">
          <span className="text-white/80">Why can't I see existing passwords?</span> Passwords are hashed with bcrypt before storage — a one-way encryption that even we can't reverse. This protects your team if the database is ever compromised. To recover access, use <span className="text-white/80">Reset password</span>: a new one is generated and shown once.
        </p>
      </div>

      {showAdd && <AddAdminModal onClose={() => setShowAdd(false)} onCreated={(data) => { setShowAdd(false); setCreds({ email: data.member.email, password: data.password }); fetchTeam(); }} />}
      {creds && <CredentialsModal creds={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}
